import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { ApiMethod, AuthEndPoints } from '../../helper/apiconstant';
import { Httpservice } from '../../services/httpservice';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './forgot-password.html',
  styleUrl: './forgot-password.scss',
})
export class ForgotPassword {
  protected readonly logoPath =
    'assets/images/authentication/nea-logo.png';

  protected readonly form: FormGroup;
  protected error = '';
  protected success = '';
  protected isSubmitting = false;
  protected resendCooldownSeconds = 0;
  protected canResend = true;

  private resendTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly httpSvc: Httpservice,
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  ngOnDestroy(): void {
    this.clearResendTimer();
  }

  protected sendMail(): void {
    if (!this.canResend || this.isSubmitting) {
      return;
    }

    this.error = '';
    this.success = '';

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = {
      email: String(this.form.getRawValue().email ?? '').trim(),
    };

    this.isSubmitting = true;
    this.clearResendTimer();
    this.httpSvc
      .requestCall(AuthEndPoints.FORGOT_PASSWORD_MAIL, ApiMethod.POST, payload)
      .subscribe({
        next: (response) => {
          this.isSubmitting = false;
          if (response?.status === 'TRUE') {
            this.success = 'If the email exists, a reset link has been sent.';
            this.canResend = true;
            this.resendCooldownSeconds = 0;
            this.form.reset();
            return;
          }

          this.error = this.resolveError(response);
          this.startResendCooldown();
        },
        error: (error) => {
          this.isSubmitting = false;
          this.error = this.resolveError(error?.error ?? error);
          this.startResendCooldown();
        },
      });
  }

  protected goToSignin(): void {
    this.router.navigate(['/signin']);
  }

  private resolveError(response: any): string {
    const message = typeof response?.msg === 'string' ? response.msg.trim() : '';
    if (message.toLowerCase().includes('unable to send mail')) {
      return 'Mail not sent. Please try again.';
    }

    if (message) {
      return message;
    }

    return 'Mail not sent. Please try again.';
  }

  private startResendCooldown(): void {
    this.clearResendTimer();
    this.canResend = false;
    this.resendCooldownSeconds = 60;

    this.resendTimer = setInterval(() => {
      this.resendCooldownSeconds -= 1;
      if (this.resendCooldownSeconds <= 0) {
        this.clearResendTimer();
        this.canResend = true;
        this.resendCooldownSeconds = 0;
      }
    }, 1000);
  }

  private clearResendTimer(): void {
    if (this.resendTimer !== null) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
  }
}
